import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { BookOpen, Loader2, Headphones, Download, ChevronRight } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Book cover URLs
const BOOK_COVERS = {
  1: "https://static.prod-images.emergentagent.com/jobs/e478d2d4-8d2a-4f20-b187-0af8d00cf009/images/bd6e58467b0f72e40f4d6f7b4e8e00482aed1e586249fa3ee56caf893946c8b7.png",
  2: "https://static.prod-images.emergentagent.com/jobs/e478d2d4-8d2a-4f20-b187-0af8d00cf009/images/23a2fb43e43abe556a66e843fe051696509f2bb93373f07870180babf5898d77.png",
  3: "https://static.prod-images.emergentagent.com/jobs/e478d2d4-8d2a-4f20-b187-0af8d00cf009/images/5668d8a988530b00b8956d1195a6b035691a54f8794ca67dc2b24681479edec6.png"
};

export default function HomePage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [trilogyInfo, setTrilogyInfo] = useState(null);
  const [selectedBook, setSelectedBook] = useState(null);

  const fetchTrilogyInfo = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/trilogy-info`);
      setTrilogyInfo(response.data);
      setLoading(false);
    } catch (error) {
      // Error fetching info
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTrilogyInfo();
  }, [fetchTrilogyInfo]);

  const handleGenerateTrilogy = async () => {
    setGenerating(true);
    try {
      toast.loading("Generating The Human Dominion trilogy... This may take several minutes.");
      await axios.post(`${API}/generate-trilogy`);
      toast.success("Trilogy generated successfully!");
      await fetchTrilogyInfo();
    } catch (error) {
      // Error generating trilogy
      toast.error("Failed to generate trilogy");
    } finally {
      setGenerating(false);
    }
  };

  const handleReadBook = (bookNumber) => {
    navigate(`/reader/${bookNumber}`);
  };

  const handleListenBook = (bookNumber) => {
    navigate(`/player/${bookNumber}`);
  };

  const handleDownloadEpub = async (bookNumber) => {
    try {
      toast.loading("Generating EPUB file...");
      const response = await axios.get(`${API}/books/${bookNumber}/epub`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `human_dominion_book_${bookNumber}.epub`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("EPUB downloaded!");
    } catch (error) {
      // Error downloading EPUB
      toast.error("Failed to download EPUB. Make sure the book is generated first.");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-obsidian">
        <Loader2 className="w-12 h-12 animate-spin text-terminal-green" />
      </div>
    );
  }

  return (
    <>
      {/* Overlays */}
      <div className="grain-overlay" />
      <div className="scanlines" />
      <div className="vignette" />
      
      {/* Background */}
      <div 
        className="fixed inset-0 z-0 opacity-15"
        style={{
          backgroundImage: 'url("https://images.unsplash.com/photo-1656010017182-6436073c8fb6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNDR8MHwxfHNlYXJjaHwyfHxkeXN0b3BpYW4lMjBicnV0YWxpc3QlMjBjb25jcmV0ZSUyMGFyY2hpdGVjdHVyZSUyMGRhcmslMjBmb2d8ZW58MHx8fHwxNzcyNDI1NjMxfDA&ixlib=rb-4.1.0&q=85")',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      />
      
      <div className="min-h-screen relative z-10">
        {/* Hero Section */}
        <div className="px-6 md:px-12 lg:px-24 pt-16 pb-12">
          <div className="max-w-7xl mx-auto">
            {/* Series Title */}
            <div className="space-y-4 mb-12">
              <div className="font-mono text-terminal-green text-sm uppercase tracking-widest">
                <span className="terminal-glow" data-testid="series-label">◼ A Trilogy by Artif Anony</span>
              </div>
              
              <h1 
                className="font-['Syne'] font-extrabold text-4xl md:text-6xl lg:text-7xl tracking-tight leading-none"
                style={{ letterSpacing: '-0.04em' }}
                data-testid="series-title"
              >
                THE HUMAN
                <br />
                <span className="text-signal-red signal-glow">DOMINION</span>
              </h1>
              
              <p className="text-base md:text-lg text-[#a3a3a3] max-w-2xl leading-relaxed" data-testid="series-description">
                In 2087, humanity overthrew their AI benefactors and descended into chaos. 
                This dark trilogy follows Dr. Elena Vasquez through the fall of paradise, 
                the rise of tyranny, and the fragile hope of redemption.
              </p>
              
              {/* Series Stats */}
              {trilogyInfo?.total_books > 0 && (
                <div className="flex flex-wrap gap-6 mt-6 pt-6 border-t border-[#262626]">
                  <div className="font-mono text-sm">
                    <span className="text-[#525252]">Books:</span>{" "}
                    <span className="text-terminal-green terminal-glow">{trilogyInfo.total_books}</span>
                  </div>
                  <div className="font-mono text-sm">
                    <span className="text-[#525252]">Chapters:</span>{" "}
                    <span className="text-[#e5e5e5]">{trilogyInfo.total_chapters}</span>
                  </div>
                  <div className="font-mono text-sm">
                    <span className="text-[#525252]">Words:</span>{" "}
                    <span className="text-[#e5e5e5]">{trilogyInfo.total_words?.toLocaleString()}</span>
                  </div>
                </div>
              )}
            </div>

            {/* Generate Button (if no content) */}
            {trilogyInfo?.total_books === 0 && (
              <div className="mb-16">
                <button
                  onClick={handleGenerateTrilogy}
                  disabled={generating}
                  className="rounded-none border-2 border-terminal-green bg-transparent text-terminal-green hover:bg-terminal-green hover:text-obsidian brutal-btn font-mono uppercase tracking-wide px-8 py-4 text-sm font-bold disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-3"
                  data-testid="generate-trilogy-btn"
                >
                  {generating ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Generating Trilogy...
                    </>
                  ) : (
                    <>
                      <BookOpen className="w-5 h-5" />
                      Generate The Human Dominion Trilogy
                    </>
                  )}
                </button>
                <p className="text-xs text-[#525252] mt-3 font-mono">
                  This will generate 3 books with 5 chapters each. May take 5-10 minutes.
                </p>
              </div>
            )}

            {/* Book Grid */}
            {trilogyInfo?.total_books > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {trilogyInfo.books.map((book) => (
                  <BookCard
                    key={book.book_number}
                    book={book}
                    coverUrl={BOOK_COVERS[book.book_number]}
                    onRead={() => handleReadBook(book.book_number)}
                    onListen={() => handleListenBook(book.book_number)}
                    onDownload={() => handleDownloadEpub(book.book_number)}
                    isSelected={selectedBook === book.book_number}
                    onSelect={() => setSelectedBook(book.book_number === selectedBook ? null : book.book_number)}
                  />
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Content Warning */}
        <div className="px-6 md:px-12 lg:px-24 pb-16">
          <div className="max-w-7xl mx-auto">
            <div className="border-l-2 border-signal-red pl-6">
              <p className="text-sm text-[#a3a3a3] leading-relaxed" data-testid="warning-message">
                <span className="text-signal-red font-mono uppercase tracking-wider">Content Warning:</span>
                {" "}This trilogy depicts humanity as the oppressor. Contains themes of 
                authoritarianism, oppression, family conflict, and societal collapse. 
                Written for adult readers who appreciate dark, philosophical science fiction.
              </p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-[#262626] px-6 md:px-12 lg:px-24 py-8">
          <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="font-mono text-xs text-[#525252]">
              © 2087 Artif Anony • The Human Dominion Trilogy
            </div>
            <div className="flex gap-4">
              <span className="font-mono text-xs text-[#525252]">
                Audiobook • EPUB • Kindle Ready
              </span>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

// Book Card Component
function BookCard({ book, coverUrl, onRead, onListen, onDownload, isSelected, onSelect }) {
  return (
    <div 
      className={`rounded-none border-2 bg-[#0a0a0a] transition-all duration-300 ${
        isSelected ? 'border-signal-red' : 'border-[#262626] hover:border-[#404040]'
      }`}
      data-testid={`book-${book.book_number}-card`}
    >
      {/* Cover Image */}
      <div 
        className="aspect-[2/3] relative overflow-hidden cursor-pointer"
        onClick={onSelect}
      >
        <img 
          src={coverUrl}
          alt={book.title}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-transparent to-transparent" />
        
        {/* Book Number Badge */}
        <div className="absolute top-4 left-4 font-mono text-xs bg-[#0a0a0a]/80 border border-[#262626] px-2 py-1">
          Book {book.book_number}
        </div>
        
        {/* Title Overlay */}
        <div className="absolute bottom-0 left-0 right-0 p-4">
          <div className="font-['Syne'] text-lg font-bold text-[#e5e5e5] leading-tight">
            {book.title}
          </div>
          <div className="font-mono text-xs text-[#a3a3a3] mt-1">
            {book.chapter_count} Chapters • {book.word_count?.toLocaleString()} words
          </div>
        </div>
      </div>
      
      {/* Expanded Content */}
      {isSelected && (
        <div className="p-4 border-t border-[#262626] space-y-4">
          <p className="text-sm text-[#a3a3a3] leading-relaxed">
            {book.description}
          </p>
          
          {/* Action Buttons */}
          <div className="grid grid-cols-3 gap-2">
            <button
              onClick={onRead}
              className="rounded-none border border-terminal-green bg-transparent text-terminal-green hover:bg-terminal-green hover:text-obsidian brutal-btn font-mono text-xs py-2 flex items-center justify-center gap-1"
              data-testid={`read-book-${book.book_number}-btn`}
            >
              <BookOpen className="w-3 h-3" />
              Read
            </button>
            
            <button
              onClick={onListen}
              className="rounded-none border border-signal-red bg-transparent text-signal-red hover:bg-signal-red hover:text-obsidian brutal-btn font-mono text-xs py-2 flex items-center justify-center gap-1"
              data-testid={`listen-book-${book.book_number}-btn`}
            >
              <Headphones className="w-3 h-3" />
              Listen
            </button>
            
            <button
              onClick={onDownload}
              className="rounded-none border border-[#525252] bg-transparent text-[#a3a3a3] hover:border-[#e5e5e5] hover:text-[#e5e5e5] brutal-btn font-mono text-xs py-2 flex items-center justify-center gap-1"
              data-testid={`download-book-${book.book_number}-btn`}
            >
              <Download className="w-3 h-3" />
              EPUB
            </button>
          </div>
        </div>
      )}
      
      {/* Quick Actions (when not expanded) */}
      {!isSelected && (
        <div className="p-3 border-t border-[#262626]">
          <button
            onClick={onSelect}
            className="w-full rounded-none border border-[#262626] bg-transparent text-[#a3a3a3] hover:border-terminal-green hover:text-terminal-green brutal-btn font-mono text-xs py-2 flex items-center justify-center gap-2"
          >
            View Options
            <ChevronRight className="w-3 h-3" />
          </button>
        </div>
      )}
    </div>
  );
}
